import {
  Fragment,
  type NodeType,
  type Node as ProseMirrorNode,
  type ResolvedPos,
  type Schema,
} from '@tiptap/pm/model';
import { TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';
import { DRAFT_SECTION_SENTINEL } from './draft-sentinel';
import { findSectionDepth } from './find-section-depth';

interface SectionNodeTypes {
  heading: NodeType;
  paragraph: NodeType;
  sectionNode: NodeType;
}

/**
 * Turns a slice of selected content into valid section-body blocks. A slice can
 * surface three kinds of node:
 * - inline nodes (the selection cut through a paragraph) — wrapped in a paragraph;
 * - blocks the section accepts directly (paragraph, list, …) — kept as-is;
 * - loose list items (the selection cut into a list, so the list wrapper is open
 *   and absent from the slice) — re-wrapped in `listWrapper`, the source list.
 *
 * Inline marks and hard breaks are preserved. `dropEmptyParagraphs` removes the
 * empty paragraph a slice leaves at a block-content edge (a remnant artifact).
 */
function sliceToBodyBlocks(
  content: Fragment,
  types: SectionNodeTypes,
  listWrapper: ProseMirrorNode | null,
  dropEmptyParagraphs: boolean
): ProseMirrorNode[] {
  const { paragraph, sectionNode } = types;
  const blocks: ProseMirrorNode[] = [];
  let inlineRun: ProseMirrorNode[] = [];
  let looseItems: ProseMirrorNode[] = [];
  const flushInline = () => {
    if (inlineRun.length > 0) {
      blocks.push(paragraph.createChecked(null, Fragment.fromArray(inlineRun)));
      inlineRun = [];
    }
  };
  const flushLooseItems = () => {
    if (looseItems.length > 0 && listWrapper) {
      blocks.push(
        listWrapper.type.createChecked(listWrapper.attrs, Fragment.fromArray(looseItems))
      );
    }
    looseItems = [];
  };

  content.forEach((node) => {
    if (node.isInline) {
      flushLooseItems();
      inlineRun.push(node);
      return;
    }
    flushInline();
    if (sectionNode.contentMatch.matchType(node.type)) {
      flushLooseItems();
      blocks.push(node);
    } else {
      // A node the section can't hold directly (e.g. a list item) — accumulate
      // to re-wrap in its source list.
      looseItems.push(node);
    }
  });
  flushInline();
  flushLooseItems();

  // Slicing to a block edge leaves an empty paragraph or list behind; those are
  // cut artifacts, not real remnant content, so drop text-empty (non-atom) blocks.
  return dropEmptyParagraphs
    ? blocks.filter((block) => block.isAtom || block.textContent.trim() !== '')
    : blocks;
}

/**
 * Seeds the section title from a bold lead-in. When the body's first block is a
 * paragraph whose first line opens with bold text, that bold run becomes the
 * title — trailing colon stripped — and is dropped from the body (so it isn't
 * duplicated under the heading); the rest of the line, if any, stays in the
 * body. Returns `null` — leaving the title for manual entry — when the line
 * doesn't start bold (plain or mid-text bold), the first block isn't a
 * paragraph (e.g. a list), or the bold run is only whitespace.
 */
function extractBoldTitleFromBody(
  schema: Schema,
  { paragraph }: SectionNodeTypes,
  bodyBlocks: ProseMirrorNode[]
): { title: string; body: ProseMirrorNode[] } | null {
  const bold = schema.marks.bold;
  if (!bold) return null;

  const [first, ...rest] = bodyBlocks;
  if (!first || first.type !== paragraph) return null;

  // Split the first paragraph at its first hard break into lead line + remainder.
  const leadNodes: ProseMirrorNode[] = [];
  const remainderNodes: ProseMirrorNode[] = [];
  let lineBreak: ProseMirrorNode | null = null;
  first.content.forEach((node) => {
    if (!lineBreak && node.type.name === 'hardBreak') {
      lineBreak = node;
      return;
    }
    (lineBreak ? remainderNodes : leadNodes).push(node);
  });

  // The title is the lead line's opening run of bold text, ending at the first
  // non-bold node; anything after the run stays in the body.
  let runLength = 0;
  for (const node of leadNodes) {
    if (!node.isText || !node.marks.some((m) => m.type === bold)) break;
    runLength++;
  }
  if (runLength === 0) return null;

  // A trailing colon is label punctuation (`**Allergies:**`), not title text.
  const title = leadNodes
    .slice(0, runLength)
    .map((node) => node.text ?? '')
    .join('')
    .replace(DRAFT_SECTION_SENTINEL, '')
    .trim()
    .replace(/\s*:$/, '');
  if (!title) return null;

  // Drop the bold run, along with the whitespace separating it from the rest of
  // the line.
  const lineRest = leadNodes.slice(runLength);
  const firstRest = lineRest[0];
  if (firstRest?.isText) {
    const trimmed = (firstRest.text ?? '').replace(/^\s+/, '');
    if (trimmed) lineRest[0] = schema.text(trimmed, firstRest.marks);
    else lineRest.shift();
  }

  const firstContent = [...lineRest];
  if (remainderNodes.length > 0) {
    if (firstContent.length > 0 && lineBreak) firstContent.push(lineBreak);
    firstContent.push(...remainderNodes);
  }
  const body =
    firstContent.length > 0
      ? [paragraph.createChecked(first.attrs, Fragment.fromArray(firstContent)), ...rest]
      : rest;
  return { title, body };
}

/**
 * Builds a section from `bodyBlocks`. The heading is seeded from a bold lead-in
 * when present (see `extractBoldTitleFromBody`); otherwise it carries the
 * sentinel-only draft shape `splitSectionAtParagraph` produces for a carried-down
 * body, so the node-view's placeholder, draft-promotion, and blur-cleanup behave
 * identically.
 */
function buildSectionFromBody(
  schema: Schema,
  types: SectionNodeTypes,
  bodyBlocks: ProseMirrorNode[]
): ProseMirrorNode {
  const { heading, paragraph, sectionNode } = types;
  const titled = extractBoldTitleFromBody(schema, types, bodyBlocks);
  const headingNode = heading.createChecked(
    { level: 1 },
    schema.text(titled ? titled.title : DRAFT_SECTION_SENTINEL)
  );
  const bodyBlocksToUse = titled ? titled.body : bodyBlocks;
  const body = bodyBlocksToUse.length > 0 ? bodyBlocksToUse : [paragraph.createChecked()];
  return sectionNode.createChecked(
    { sectionTitle: titled ? titled.title : 'New Section', isDraftSection: false },
    Fragment.fromArray([headingNode, ...body])
  );
}

/**
 * Cursor landing inside a new section's heading: after a seeded title so the
 * user can adjust it, at the content start when it's the draft sentinel.
 * `+1` enters the section, `+1` the heading.
 */
function headingSelectionPos(section: ProseMirrorNode, sectionStart: number): number {
  const headingNode = section.child(0);
  const seeded = headingNode.textContent !== DRAFT_SECTION_SENTINEL;
  return sectionStart + 2 + (seeded ? headingNode.content.size : 0);
}

/**
 * The body-level block enclosing `$pos` when it's a list (not a textblock), so
 * loose list items sliced out of it can be re-wrapped. `bodyDepth` is the depth
 * of a section/doc body block.
 */
function enclosingListAt($pos: ResolvedPos, bodyDepth: number): ProseMirrorNode | null {
  if ($pos.depth < bodyDepth) return null;
  const block = $pos.node(bodyDepth);
  return block.isTextblock ? null : block;
}

/**
 * Snaps a selection edge to a whole list item when it falls inside a list, so
 * slicing yields complete items instead of empty-item boundary artifacts. Edges
 * inside a textblock (a paragraph's exact offset) are left untouched.
 */
function snapToListItem(
  $pos: ResolvedPos,
  bodyDepth: number,
  edge: 'start' | 'end'
): number {
  if (!enclosingListAt($pos, bodyDepth)) return $pos.pos;
  const itemDepth = bodyDepth + 1;
  return edge === 'start' ? $pos.before(itemDepth) : $pos.after(itemDepth);
}

export interface SectionSplit {
  sectionStart: number;
  sectionEnd: number;
  section: ProseMirrorNode;
  headingNode: ProseMirrorNode;
  beforeBlocks: ProseMirrorNode[];
  selectedBlocks: ProseMirrorNode[];
  afterBlocks: ProseMirrorNode[];
}

/**
 * Resolves how the selection would split its enclosing section: the body blocks
 * before, inside, and after the selected range. Returns `null` when the range
 * can't map to a body split (no heading, selection reaching into the heading,
 * or nothing selectable).
 */
export function computeSectionSplit(
  state: EditorState,
  sectionDepth: number,
  types: SectionNodeTypes
): SectionSplit | null {
  const { doc, selection } = state;
  const { from, to } = selection;
  const $from = doc.resolve(from);
  const $to = doc.resolve(to);
  const section = $from.node(sectionDepth);
  const headingNode = section.child(0);
  if (headingNode.type.name !== 'heading') return null;

  const sectionStart = $from.before(sectionDepth);
  const sectionEnd = $from.after(sectionDepth);
  const bodyStart = sectionStart + 1 + headingNode.nodeSize;
  const bodyEnd = sectionEnd - 1;

  // A selection that reaches into the heading can't map to a body section.
  if (from < bodyStart) return null;

  const bodyDepth = sectionDepth + 1;
  const fromList = enclosingListAt($from, bodyDepth);
  const toList = enclosingListAt($to, bodyDepth);
  const selStart = snapToListItem($from, bodyDepth, 'start');
  const selEnd = snapToListItem($to, bodyDepth, 'end');
  const beforeBlocks = sliceToBodyBlocks(
    doc.slice(bodyStart, selStart).content,
    types,
    fromList,
    true
  );
  const selectedBlocks = sliceToBodyBlocks(
    doc.slice(selStart, selEnd).content,
    types,
    fromList,
    false
  );
  const afterBlocks = sliceToBodyBlocks(
    doc.slice(selEnd, bodyEnd).content,
    types,
    toList,
    true
  );
  if (selectedBlocks.length === 0) return null;

  return {
    sectionStart,
    sectionEnd,
    section,
    headingNode,
    beforeBlocks,
    selectedBlocks,
    afterBlocks,
  };
}

/**
 * A selection covering a section's entire body while its heading holds no real
 * title (empty or draft-sentinel-only). Converting it wouldn't restructure
 * anything — it would re-create the same section and leave an empty untitled
 * one behind — so the conversion is refused and menus hide the button.
 */
export function isFullBodyOfUntitledSection(split: SectionSplit): boolean {
  const title = split.headingNode.textContent
    .replaceAll(DRAFT_SECTION_SENTINEL, '')
    .trim();
  return (
    title === '' && split.beforeBlocks.length === 0 && split.afterBlocks.length === 0
  );
}

/**
 * Splits the section containing the selection into siblings: the body before the
 * selection stays in the original section, the selection becomes a new section,
 * and any body after the selection becomes a trailing section. Operates on the
 * exact selected range, so a selection inside a multi-line paragraph extracts
 * only the highlighted lines and a selection across list items keeps them listed.
 * Returns `null` when the range can't map to a body split, or when the split
 * would be redundant (the entire body of an untitled section).
 */
function splitSectionAroundSelection(
  state: EditorState,
  sectionDepth: number,
  types: SectionNodeTypes
): Transaction | null {
  const split = computeSectionSplit(state, sectionDepth, types);
  if (!split || isFullBodyOfUntitledSection(split)) return null;
  const {
    sectionStart,
    sectionEnd,
    section,
    headingNode,
    beforeBlocks,
    selectedBlocks,
    afterBlocks,
  } = split;
  const { schema } = state;
  const { paragraph, sectionNode } = types;

  // A section needs a body block, so pad an empty paragraph when only the
  // heading would remain in the original.
  const originalChildren = [headingNode, ...beforeBlocks];
  if (originalChildren.length <= 1) originalChildren.push(paragraph.createChecked());
  const originalSection = sectionNode.createChecked(
    section.attrs,
    Fragment.fromArray(originalChildren)
  );

  const newSection = buildSectionFromBody(schema, types, selectedBlocks);
  const sections = [originalSection, newSection];
  if (afterBlocks.length > 0) {
    sections.push(buildSectionFromBody(schema, types, afterBlocks));
  }

  const { tr } = state;
  tr.replaceWith(sectionStart, sectionEnd, Fragment.fromArray(sections));
  const newSectionStart = sectionStart + originalSection.nodeSize;
  tr.setSelection(
    TextSelection.create(tr.doc, headingSelectionPos(newSection, newSectionStart))
  );
  return tr;
}

/**
 * Wraps a top-level selection in a new section. Content before and after the
 * selection within the touched blocks stays as top-level siblings.
 */
function wrapTopLevelSelection(
  state: EditorState,
  $from: ResolvedPos,
  $to: ResolvedPos,
  types: SectionNodeTypes
): Transaction | null {
  const { doc, schema } = state;
  // The span of top-level blocks the selection touches.
  const touchedStart = $from.before(1);
  const touchedEnd = $to.after(1);

  // Wrapping an existing section would nest sections, which the editor flattens
  // on reload — leave the selection untouched instead.
  for (let pos = touchedStart; pos < touchedEnd; ) {
    const node = doc.nodeAt(pos);
    if (!node) break;
    if (node.type.name === 'sectionNode') return null;
    pos += node.nodeSize;
  }

  const fromList = enclosingListAt($from, 1);
  const toList = enclosingListAt($to, 1);
  const selStart = snapToListItem($from, 1, 'start');
  const selEnd = snapToListItem($to, 1, 'end');
  const beforeBlocks = sliceToBodyBlocks(
    doc.slice(touchedStart, selStart).content,
    types,
    fromList,
    true
  );
  const selectedBlocks = sliceToBodyBlocks(
    doc.slice(selStart, selEnd).content,
    types,
    fromList,
    false
  );
  const afterBlocks = sliceToBodyBlocks(
    doc.slice(selEnd, touchedEnd).content,
    types,
    toList,
    true
  );
  if (selectedBlocks.length === 0) return null;

  const newSection = buildSectionFromBody(schema, types, selectedBlocks);

  const { tr } = state;
  tr.replaceWith(
    touchedStart,
    touchedEnd,
    Fragment.fromArray([...beforeBlocks, newSection, ...afterBlocks])
  );
  const sectionStart = beforeBlocks.reduce(
    (pos, block) => pos + block.nodeSize,
    touchedStart
  );
  tr.setSelection(
    TextSelection.create(tr.doc, headingSelectionPos(newSection, sectionStart))
  );
  return tr;
}

/**
 * Builds the transaction that turns the selection into a new section, or `null`
 * when it can't map cleanly (see the app-side `convertSelectionToSection`). The
 * selected content becomes the new section's body, with an empty heading to
 * name. A selection inside one section splits that section around it; a
 * top-level selection is wrapped in place.
 */
export function applyConvertSelectionToSection(state: EditorState): Transaction | null {
  const { schema } = state;
  const { heading, paragraph, sectionNode } = schema.nodes;
  if (!heading || !paragraph || !sectionNode) {
    // Unreachable from the shell (it pre-checks and logs); fail null silently
    // so this pure builder carries no application logging (task 4.10).
    return null;
  }
  const types: SectionNodeTypes = { heading, paragraph, sectionNode };

  const { from, to } = state.selection;
  if (from === to) return null;

  const $from = state.doc.resolve(from);
  const $to = state.doc.resolve(to);
  const fromSectionDepth = findSectionDepth($from);
  const toSectionDepth = findSectionDepth($to);

  // Both ends inside the same section → split it around the selection.
  if (fromSectionDepth !== null && toSectionDepth !== null) {
    if ($from.before(fromSectionDepth) !== $to.before(toSectionDepth)) return null;
    return splitSectionAroundSelection(state, fromSectionDepth, types);
  }

  // Both ends outside any section → wrap the top-level selection.
  if (fromSectionDepth === null && toSectionDepth === null) {
    return wrapTopLevelSelection(state, $from, $to, types);
  }

  // Selection straddles a section boundary — can't map cleanly.
  return null;
}
